// public/js/base/baseViews.js

import { createLogger } from '@utils/logger.js';
import AppSettings from '@services/appSettings.js';

/**
 * BaseViews - Schema-driven UI component generation
 * ONLY handles: DOM creation, template rendering, UI assembly
 * Uses Manager schema to auto-generate forms, filters, and validation
 * NO functions, NO event listeners, NO business logic
 * NOW SUPPORTS: Configurable filtering UI (real-time vs apply button)
 */
class BaseViews {
    /**
     * @param {Object} config - Views configuration
     * @param {string} config.entityName - Name of the entity
     * @param {Object} config.manager - Manager instance for schema access
     * @param {Object} config.iconMap - Icon mapping
     * @param {Object} config.templates - Custom templates
     */
    constructor(config = {}) {
        // Remove singleton pattern to allow proper dependency injection per entity type
        this.logger = createLogger('BaseViews');
        this.entityName = config.entityName || 'entity';
        this.entityNamePlural = config.entityNamePlural || `${this.entityName}s`;
        this.manager = config.manager; // Schema access through manager
        this.iconMap = config.iconMap || this.getDefaultIconMap();
        this.templates = config.templates || {};
        
        this.logger.info(`Views initialized for ${this.entityName}`, {
            hasManager: !!this.manager,
            hasCustomTemplates: Object.keys(this.templates).length > 0,
            hasCustomIcons: config.iconMap !== undefined
        });
    }

    /**
     * Get default icon mapping
     */
    getDefaultIconMap() {
        return {
            create: 'add_circle',
            edit: 'edit',
            delete: 'delete',
            share: 'share',
            view: 'visibility',
            download: 'download',
            upload: 'upload',
            search: 'search',
            filter: 'filter_list',
            sort: 'sort',
            close: 'close',
            save: 'save',
            cancel: 'cancel',
            back: 'arrow_back',
            forward: 'arrow_forward',
            expand: 'expand_more',
            collapse: 'expand_less',
            apply: 'check',
            clear: 'clear_all'
        };
    }

    /**
     * Create main entity container
     * @param {Object} options - Container options
     * @returns {HTMLElement} Container element
     */
    createEntityContainer(options = {}) {
        const container = document.createElement('div');
        container.id = `${this.entityName}-container`;
        container.className = `${this.entityName}-container entity-container`;

        // Add title if provided (check options exists and has title)
        if (options && options.title) {
            const titleElement = document.createElement('h2');
            titleElement.className = 'entity-container-title';
            titleElement.textContent = options.title;
            container.appendChild(titleElement);
        }

        // Create header container for filter and new button
        const containerHeader = document.createElement('div');
        containerHeader.className = `${this.entityName}-container-header entity-container-header`;
        container.appendChild(containerHeader);

        this.logger.ui(`Created entity container for ${this.entityName}`);
        return container;
    }

    /**
     * Create entity cards grid container
     * @returns {HTMLElement} Grid container
     */
    createEntityCardsContainer() {
        const container = document.createElement('div');
        container.className = `${this.entityName}-cards-container entity-cards-container`;
        container.id = `${this.entityName}-cards-grid`;
        
        this.logger.ui('Created entity cards container');
        return container;
    }

    /**
     * Create entity filter container - COLLAPSIBLE WITH FILTER TAGS
     * @param {Array} filterFields - Filter field configurations from schema
     * @param {Object} filterSettings - Filter behavior settings
     * @returns {HTMLElement} Filter container
     */
    createEntityFilterContainer(filterFields = null, filterSettings = null) {
        // Get filter fields from schema if not provided
        const schemaFilterFields = filterFields || (this.manager ? this.manager.generateFilterConfig() : []);

        // Get filter settings
        const settings = filterSettings || AppSettings.getFilterSettings(this.entityName);

        const filterContainer = document.createElement('div');
        filterContainer.className = `${this.entityName}-filter-container entity-filter-container collapsible`;

        // Add filter mode class for styling
        filterContainer.classList.add(settings.realtime ? 'realtime-mode' : 'apply-mode');

        // Start collapsed by default
        filterContainer.classList.add('collapsed');

        // First line: Search + Clear + Chevron
        const firstLine = document.createElement('div');
        firstLine.className = 'filter-first-line';

        const searchGroup = this.createSearchGroup();
        firstLine.appendChild(searchGroup);

        // Clear button
        const clearButton = this.createClearFiltersButton();
        firstLine.appendChild(clearButton);

        // Chevron toggle button
        const toggleButton = document.createElement('button');
        toggleButton.className = 'filter-toggle-button';
        toggleButton.id = 'filter-toggle-btn';
        toggleButton.innerHTML = `<span class="material-symbols-outlined">${this.iconMap.expand}</span>`;
        toggleButton.setAttribute('aria-label', 'Toggle filters');
        toggleButton.setAttribute('title', 'Show/hide filters');
        firstLine.appendChild(toggleButton);

        filterContainer.appendChild(firstLine);

        // Collapsible filters section
        const filtersSection = document.createElement('div');
        filtersSection.className = 'filters-collapsible-section';

        schemaFilterFields.forEach(field => {
            const filter = this.createFilterField(field);
            filtersSection.appendChild(filter);
        });

        // Add apply button if needed (only in apply-button mode)
        if (!settings.realtime) {
            const applyButton = this.createApplyFiltersButton();
            filtersSection.appendChild(applyButton);
        }

        filterContainer.appendChild(filtersSection);

        // Filter tags container (for active filters) - AFTER collapsible section
        const tagsContainer = document.createElement('div');
        tagsContainer.className = 'filter-tags-container';
        filterContainer.appendChild(tagsContainer);

        this.logger.ui(`Created collapsible filter container with ${schemaFilterFields.length} filter fields (${settings.realtime ? 'real-time' : 'apply-button'} mode)`);
        return filterContainer;
    }


    createNewEntityButton() {
        const button = document.createElement('div');
        button.className = `${this.entityName}-new-entity-button new-entity-button`;
        // button.type = 'button';
        
        // Create icon using Material Symbols
        const icon = document.createElement('span');
        icon.className = 'material-symbols-outlined';
        icon.textContent = 'add';
        
        button.appendChild(icon);
        
        // Add accessible label
        button.setAttribute('aria-label', `Add new ${this.entityName}`);
        button.setAttribute('title', `Add new ${this.entityName}`);
        
        this.logger.ui(`Created new entity button for ${this.entityName}`);
        return button;
    }

    /**
     * Create filter actions group
     * @param {Object} settings - Filter settings
     * @returns {HTMLElement} Actions group element
     */
    createFilterActionsGroup(settings) {
        const actionsGroup = document.createElement('div');
        actionsGroup.className = 'filter-actions-group';

        // Always show clear button
        const clearButton = this.createClearFiltersButton();
        actionsGroup.appendChild(clearButton);

        // Show apply button only in apply-button mode
        if (!settings.realtime) {
            const applyButton = this.createApplyFiltersButton();
            actionsGroup.appendChild(applyButton);
        }

        return actionsGroup;
    }

    /**
     * Create apply filters button
     * @returns {HTMLElement} Apply button element
     */
    createApplyFiltersButton() {
        const applyButton = document.createElement('button');
        applyButton.className = 'apply-filters-button filter-action-button';
        applyButton.id = 'apply-filters-btn';
        applyButton.disabled = true; // Initially disabled until filters are set
        applyButton.innerHTML = `
            <span class="material-symbols-outlined">${this.iconMap.apply}</span>
            ${AppSettings.ui.applyButtonText}
        `;
        
        return applyButton;
    }

    /**
     * Create search input group
     * @returns {HTMLElement} Search group element
     */
    createSearchGroup() {
        const searchGroup = document.createElement('div');
        searchGroup.className = 'filter-group search-group';

        const searchIcon = document.createElement('span');
        searchIcon.className = 'search-icon material-symbols-outlined';
        searchIcon.id = `${this.entityName}-search-icon`;
        searchIcon.textContent = this.iconMap.search;
        searchIcon.setAttribute('role', 'button');
        searchIcon.setAttribute('tabindex', '0');
        searchIcon.setAttribute('aria-label', 'Add search term');
        searchIcon.setAttribute('title', 'Click to add search term or press Enter');
        searchIcon.style.cursor = 'pointer';

        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.id = `${this.entityName}-search`;
        searchInput.className = 'filter-input search-input';
        searchInput.placeholder = `Search ${this.entityNamePlural}... (Press Enter to add)`;

        searchGroup.appendChild(searchIcon);
        searchGroup.appendChild(searchInput);

        return searchGroup;
    }

    /**
     * Create individual filter field - SCHEMA-DRIVEN WITH HIGHLIGHT SUPPORT
     * @param {Object} fieldConfig - Filter field configuration from schema
     * @returns {HTMLElement} Filter field element
     */
    createFilterField(fieldConfig) {
        const filterGroup = document.createElement('div');
        filterGroup.className = 'filter-group';

        const label = document.createElement('label');
        label.textContent = fieldConfig.label || this.formatFieldName(fieldConfig.name);
        label.htmlFor = `${this.entityName}-${fieldConfig.name}-filter`;

        let input;

        // Handle datalist for searchable fields
        if (fieldConfig.type === 'datalist') {
            input = this.createFilterDatalist(fieldConfig);
        } else if (fieldConfig.type === 'select' && fieldConfig.options) {
            input = this.createSelectInput(fieldConfig);
        } else if (fieldConfig.type === 'date') {
            input = this.createDateInput(fieldConfig);
        } else {
            input = this.createTextInput(fieldConfig);
        }

        input.id = `${this.entityName}-${fieldConfig.name}-filter`;
        input.name = fieldConfig.name;
        input.className += ' filterable-field'; // For highlighting active filters

        filterGroup.appendChild(label);
        filterGroup.appendChild(input);

        return filterGroup;
    }

    /**
     * Create select input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Select element
     */
    createSelectInput(config) {
        const select = document.createElement('select');
        select.className = 'filter-select filter-input';
        
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = `All ${config.label || config.name}`;
        select.appendChild(defaultOption);
        
        config.options.forEach(option => {
            const optionElement = document.createElement('option');
            optionElement.value = option.value || option;
            optionElement.textContent = option.text || option;
            select.appendChild(optionElement);
        });
        
        return select;
    }

    /**
     * Create date input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Date input element
     */
    createDateInput(config) {
        const input = document.createElement('input');
        input.type = 'date';
        input.className = 'filter-input date-input';
        return input;
    }

    /**
     * Create text input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Text input element
     */
    createTextInput(config) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'filter-input';
        input.placeholder = `Filter by ${config.label || config.name}`;
        return input;
    }

    /**
     * Create datalist input for filter (HTML5 searchable select)
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Input element with datalist
     */
    createFilterDatalist(config) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'filter-input filter-datalist';
        input.placeholder = config.placeholder || `Search ${config.label || config.name}...`;
        input.setAttribute('list', `${this.entityName}-${config.name}-filter-datalist`);

        // Create datalist
        const datalist = document.createElement('datalist');
        datalist.id = `${this.entityName}-${config.name}-filter-datalist`;

        // Populate datalist with options
        const dataArray = config.searchable_data || config.options || [];
        dataArray.forEach(item => {
            const itemValue = typeof item === 'string' ? item : (item.value || item.name || item.id);
            if (itemValue === '' || itemValue === null || itemValue === undefined) {
                return;
            }

            const option = document.createElement('option');
            option.value = typeof item === 'string' ? item :
                (item.text || item.label || item.name || item.value || item.id);

            datalist.appendChild(option);
        });

        // Create container for input + datalist
        const container = document.createElement('div');
        container.className = 'datalist-container';
        container.appendChild(input);
        container.appendChild(datalist);

        this.logger.debug(`Created filter datalist for ${config.name} with ${dataArray.length} options`);

        return container;
    }

    /**
     * Create clear filters button
     * @returns {HTMLElement} Clear button element
     */
    createClearFiltersButton() {
        const clearButton = document.createElement('button');
        clearButton.className = 'clear-filters-button filter-action-button';
        clearButton.id = `clear-filters-btn`;
        clearButton.innerHTML = `
            <span class="material-symbols-outlined">${this.iconMap.clear}</span>
            ${AppSettings.ui.clearButtonText}
        `;
        
        return clearButton;
    }

    /**
     * Create entity card
     * @param {Object} cardData - Card display data (prepared by manager)
     * @param {Object} options - Card options
     * @returns {HTMLElement} Card element
     */
    createEntityCard(cardData, options = {}) {
        const entityCard = document.createElement('div');
        entityCard.id = cardData.id;
        entityCard.className = `${this.entityName}-card entity-card`;
        entityCard.dataset.entityType = this.entityName;

        if (cardData.header) {
            const header = this.createCardHeader(cardData.header);
            entityCard.appendChild(header);
        }

        const body = this.createCardBody(cardData.body);
        entityCard.appendChild(body);

        if (cardData.actions && cardData.actions.length > 0) {
            const actions = this.createCardActions(cardData.actions);
            entityCard.appendChild(actions);
        }

        this.logger.ui(`Created entity card: ${cardData.id}`);
        return entityCard;
    }

    /**
     * Create card header
     * @param {Object} headerData - Header data
     * @returns {HTMLElement} Header element
     */
    createCardHeader(headerData) {
        const header = document.createElement('div');
        header.className = `${this.entityName}-card-header entity-card-header`;

        if (headerData.imageUrl) {
            const img = document.createElement('img');
            img.src = headerData.imageUrl;
            img.className = `${this.entityName}-card-image entity-card-image`;
            img.alt = headerData.altText || '';
            header.appendChild(img);
        } else if (headerData.icon) {
            const icon = document.createElement('span');
            icon.className = 'material-symbols-outlined entity-card-icon';
            icon.textContent = headerData.icon;
            header.appendChild(icon);
        }

        if (headerData.status) {
            const statusBadge = document.createElement('div');
            statusBadge.className = `entity-status-badge ${headerData.status.type}`;
            statusBadge.textContent = headerData.status.text;
            header.appendChild(statusBadge);
        }

        return header;
    }

    /**
     * Create card body
     * @param {Object} bodyData - Body data with fields
     * @returns {HTMLElement} Body element
     */
    createCardBody(bodyData) {
        const body = document.createElement('div');
        body.className = `${this.entityName}-card-body entity-card-body`;

        if (bodyData.title) {
            const titleElement = document.createElement('h3');
            titleElement.className = `${this.entityName}-card-content entity-card-title`;
            titleElement.textContent = bodyData.title;
            body.appendChild(titleElement);
        }

        if (bodyData.fields) {
            bodyData.fields.forEach(field => {
                const fieldElement = this.createCardField(field);
                body.appendChild(fieldElement);
            });
        }

        return body;
    }

    /**
     * Create individual card field
     * @param {Object} field - Field data
     * @returns {HTMLElement} Field element
     */
    createCardField(field) {
        const fieldDiv = document.createElement('div');
        fieldDiv.className = `${this.entityName}-card-content entity-card-field ${field.name}`;

        const keyElement = document.createElement('span');
        keyElement.className = 'field-key';
        keyElement.textContent = field.label;

        const valueElement = document.createElement('span');
        valueElement.className = 'field-value';
        valueElement.textContent = field.value;

        fieldDiv.appendChild(keyElement);
        fieldDiv.appendChild(valueElement);

        return fieldDiv;
    }

    /**
     * Create card actions
     * @param {Array} actions - Array of action configurations
     * @returns {HTMLElement} Actions element
     */
    createCardActions(actions) {
        const actionsSection = document.createElement('div');
        actionsSection.className = `${this.entityName}-card-actions entity-card-actions`;

        actions.forEach(action => {
            const button = this.createActionButton(action);
            actionsSection.appendChild(button);
        });

        return actionsSection;
    }

    /**
     * Create new entity card
     * @returns {HTMLElement} New entity card
     */
    createNewEntityCard() {
        const card = document.createElement('div');
        card.className = `${this.entityName}-card-new-${this.entityName} entity-card-new`;
        
        const icon = document.createElement('div');
        icon.className = 'new-entity-icon';
        icon.innerHTML = `<span class="material-symbols-outlined">${this.iconMap.create}</span>`;
        
        const text = document.createElement('div');
        text.className = 'new-entity-text';
        text.innerHTML = `
            <h3>Create New ${this.formatEntityName()}</h3>
            <p>Add a new ${this.entityName} to your collection</p>
        `;
        
        card.appendChild(icon);
        card.appendChild(text);
        
        this.logger.ui('Created new entity card');
        return card;
    }

    /**
     * Create entity details view
     * @param {Object} detailsData - Details data (prepared by manager)
     * @returns {HTMLElement} Details element
     */
    createEntityDetails(detailsData) {
        const entityDetails = document.createElement('div');
        entityDetails.id = `${this.entityName}-details-${detailsData.id}`;
        entityDetails.className = `${this.entityName}-details entity-details`;

        if (detailsData.header) {
            const header = this.createDetailsHeader(detailsData.header);
            entityDetails.appendChild(header);
        }

        const body = this.createDetailsBody(detailsData.body);
        entityDetails.appendChild(body);

        if (detailsData.actions) {
            const actions = this.createDetailsActions(detailsData.actions);
            entityDetails.appendChild(actions);
        }

        this.logger.ui(`Created entity details view: ${detailsData.id}`);
        return entityDetails;
    }

    /**
     * Create details header
     * @param {Object} headerData - Header data
     * @returns {HTMLElement} Header element
     */
    createDetailsHeader(headerData) {
        const header = document.createElement('div');
        header.className = `${this.entityName}-details-header entity-details-header`;

        if (headerData.imageUrl) {
            const img = document.createElement('img');
            img.src = headerData.imageUrl;
            img.className = `${this.entityName}-details-image entity-details-image`;
            img.alt = headerData.altText || '';
            header.appendChild(img);
        }

        return header;
    }

    /**
     * Create details body
     * @param {Object} bodyData - Body data with title and sections
     * @returns {HTMLElement} Body element
     */
    createDetailsBody(bodyData) {
        const body = document.createElement('div');
        body.className = `${this.entityName}-details-body entity-details-body`;

        if (bodyData.title) {
            const titleContainer = this.createDetailsTitleSection(bodyData.title);
            body.appendChild(titleContainer);
        }

        if (bodyData.sections) {
            bodyData.sections.forEach(section => {
                const sectionElement = this.createDetailsSection(section);
                body.appendChild(sectionElement);
            });
        }

        return body;
    }

    /**
     * Create details title section
     * @param {Object} titleData - Title data
     * @returns {HTMLElement} Title container
     */
    createDetailsTitleSection(titleData) {
        const titleContainer = document.createElement('div');
        titleContainer.className = `${this.entityName}-details-content entity-details-content name-container`;

        const titleElement = document.createElement('h1');
        titleElement.className = `${this.entityName}-details-content entity-details-content name`;
        titleElement.textContent = titleData.main;
        titleContainer.appendChild(titleElement);

        if (titleData.subtitle) {
            const subtitleElement = document.createElement('h3');
            subtitleElement.className = `${this.entityName}-details-content entity-details-content subtitle`;
            subtitleElement.textContent = titleData.subtitle;
            titleContainer.appendChild(subtitleElement);
        }

        return titleContainer;
    }

    /**
     * Create details section
     * @param {Object} sectionData - Section data
     * @returns {HTMLElement} Section element
     */
    createDetailsSection(sectionData) {
        const section = document.createElement('div');
        section.className = `${this.entityName}-details-section entity-details-section ${sectionData.className}`;

        const title = document.createElement('h3');
        title.className = 'section-title';
        title.textContent = sectionData.title;
        section.appendChild(title);

        sectionData.fields.forEach(field => {
            const fieldElement = this.createDetailField(field);
            section.appendChild(fieldElement);
        });

        return section;
    }

    /**
     * Create individual detail field
     * @param {Object} field - Field data
     * @returns {HTMLElement} Field element
     */
    createDetailField(field) {
        const div = document.createElement('div');
        div.className = `${this.entityName}-details-content entity-details-field ${field.name}`;

        const keyElement = document.createElement('p');
        keyElement.className = 'key';
        keyElement.textContent = field.label;

        const valueElement = document.createElement('p');
        valueElement.className = 'value';
        valueElement.textContent = field.value;

        div.appendChild(keyElement);
        div.appendChild(valueElement);

        return div;
    }

    /**
     * Create details actions
     * @param {Array} actions - Array of action configurations
     * @returns {HTMLElement} Actions element
     */
    createDetailsActions(actions) {
        const actionsSection = document.createElement('div');
        actionsSection.className = `${this.entityName}-details-actions entity-details-actions`;

        actions.forEach(action => {
            const button = this.createActionButton(action, true);
            actionsSection.appendChild(button);
        });

        return actionsSection;
    }

    /**
     * Create entity edit form - NOW SCHEMA-DRIVEN
     * @param {Object} formData - Form data (prepared by manager) or entity object
     * @returns {HTMLElement} Form container
     */
    createEntityEditView(formData) {
        const editView = document.createElement('div');
        editView.className = `${this.entityName}-edit-view entity-edit-view`;

        // Use schema-driven form creation
        const form = this.createEntityForm(formData.fields, formData.buttons, formData);
        editView.appendChild(form);

        this.logger.ui(`Created schema-driven entity edit form: ${formData.title}`);
        return editView;
    }

    /**
     * Create entity form - NOW SCHEMA-DRIVEN
     * @param {Array} fields - Array of field configurations from schema
     * @param {Array} buttons - Array of button configurations
     * @param {Object} formData - Full form data including title and subtitle
     * @returns {HTMLElement} Form container
     */
    createEntityForm(fields, buttons, formData = {}) {
        const formContainer = document.createElement('div');
        formContainer.className = `${this.entityName}-edit-content entity-edit-content`;
        formContainer.classList.add(`${this.entityName}-form`); // For form validation

        // Add header at the top of form content
        if (formData.title) {
            const header = document.createElement('div');
            header.className = 'form-header';

            // Icon
            const iconContainer = document.createElement('div');
            iconContainer.className = 'form-icon-container';
            const icon = document.createElement('span');
            icon.className = 'material-symbols-outlined form-icon';
            // Use entity-specific icon or default to 'edit'
            icon.textContent = this.iconMap[this.entityName] || 'edit';
            iconContainer.appendChild(icon);
            header.appendChild(iconContainer);

            // Text container for title and subtitle
            const textContainer = document.createElement('div');
            textContainer.className = 'form-text-container';

            // Title
            const title = document.createElement('h2');
            title.className = 'form-title';
            title.textContent = formData.title;
            textContainer.appendChild(title);

            // Subtitle (optional)
            if (formData.subtitle) {
                const subtitle = document.createElement('p');
                subtitle.className = 'form-subtitle';
                subtitle.textContent = formData.subtitle;
                textContainer.appendChild(subtitle);
            }

            header.appendChild(textContainer);
            formContainer.appendChild(header);
        }

        // Generate form fields from schema
        fields.forEach(field => {
            const fieldElement = this.createFormField(field);
            formContainer.appendChild(fieldElement);
        });

        const buttonContainer = this.createFormButtons(buttons);
        formContainer.appendChild(buttonContainer);

        this.logger.ui(`Created schema-driven form with ${fields.length} fields and ${buttons.length} buttons`);
        return formContainer;
    }

    /**
     * Create individual form field - NOW SCHEMA-DRIVEN
     * @param {Object} fieldConfig - Field configuration from schema
     * @returns {HTMLElement} Field container
     */
    createFormField(fieldConfig) {
        const fieldContainer = document.createElement('div');
        fieldContainer.className = 'form-field-container';

        // For hidden fields, don't show label or help text
        if (fieldConfig.type === 'hidden') {
            fieldContainer.className = 'form-field-container form-field-hidden';
            fieldContainer.style.display = 'none';
            const input = this.createFormInput(fieldConfig);
            fieldContainer.appendChild(input);
            return fieldContainer;
        }

        const label = document.createElement('label');
        label.textContent = `${fieldConfig.label}${fieldConfig.required ? ' *' : ''}`;
        label.setAttribute('for', `${this.entityName}-edit-${fieldConfig.name}`);
        fieldContainer.appendChild(label);

        const input = this.createFormInput(fieldConfig);
        fieldContainer.appendChild(input); // No special logic needed!

        if (fieldConfig.helpText) {
            const helpText = document.createElement('div');
            helpText.className = 'form-help-text';
            helpText.textContent = fieldConfig.helpText;
            fieldContainer.appendChild(helpText);
        }

        return fieldContainer;
    }

    /**
     * Create form input element - NOW SCHEMA-DRIVEN WITH VALIDATION
     * @param {Object} fieldConfig - Field configuration from schema
     * @returns {HTMLElement} Input element
     */
    createFormInput(fieldConfig) {
        let input;

        switch (fieldConfig.type) {
            case 'hidden':
                input = document.createElement('input');
                input.type = 'hidden';
                input.value = fieldConfig.value || '';
                input.id = `${this.entityName}-edit-${fieldConfig.name}`;
                input.name = fieldConfig.name;
                return input; // Return early, no need for further processing

            case 'select':
                if(fieldConfig.searchable){
                    // Use HTML5 datalist for searchable select
                    input = this.createFormInputWithDatalist(fieldConfig);
                    return input; // Return the container with input + datalist
                } else {
                    input = this.createFormSelect(fieldConfig);
                }
                break;
            case 'textarea':
                input = this.createFormTextarea(fieldConfig);
                break;
            case 'checkbox':
                input = this.createFormCheckbox(fieldConfig);
                break;
            case 'number':
            case 'integer':
                input = this.createFormNumber(fieldConfig);
                break;
            case 'date':
            case 'datetime-local':
                input = this.createFormDate(fieldConfig);
                break;
            case 'email':
                input = this.createFormEmail(fieldConfig);
                break;
            case 'url':
                input = this.createFormUrl(fieldConfig);
                break;
            default:
                input = this.createFormInputBasic(fieldConfig);
        }

        input.id = `${this.entityName}-edit-${fieldConfig.name}`;
        input.name = fieldConfig.name;
        input.className = 'form-input';
        this.applyValidationAttributes(input, fieldConfig);

        return input;
    }

    /**
     * Apply validation attributes from schema to input element
     * @param {HTMLElement} input - Input element
     * @param {Object} fieldConfig - Field configuration from schema
     */
    applyValidationAttributes(input, fieldConfig) {
        // Required field
        if (fieldConfig.required) {
            input.required = true;
        }

        // String length validation
        if (fieldConfig.minLength !== undefined) {
            input.minLength = fieldConfig.minLength;
        }
        if (fieldConfig.maxLength !== undefined) {
            input.maxLength = fieldConfig.maxLength;
        }

        // Number range validation
        if (fieldConfig.min !== undefined) {
            input.min = fieldConfig.min;
        }
        if (fieldConfig.max !== undefined) {
            input.max = fieldConfig.max;
        }
        if (fieldConfig.step !== undefined) {
            input.step = fieldConfig.step;
        }

        // Pattern validation
        if (fieldConfig.pattern) {
            input.pattern = fieldConfig.pattern;
        }

        // Placeholder with metric units
        if (fieldConfig.placeholder) {
            input.placeholder = fieldConfig.placeholder;
        } else if (fieldConfig.type === 'number' || fieldConfig.type === 'integer') {
            // Add metric unit hints for number fields based on field name
            if (fieldConfig.name.includes('weight')) {
                input.placeholder = `Enter weight in kg`;
            } else if (fieldConfig.name.includes('height') || fieldConfig.name.includes('length')) {
                input.placeholder = `Enter measurement in cm`;
            } else if (fieldConfig.name.includes('distance')) {
                input.placeholder = `Enter distance in m`;
            } else if (fieldConfig.name.includes('temperature')) {
                input.placeholder = `Enter temperature in °C`;
            }
        }

        // Real-time validation
        if (this.manager) {
            input.setAttribute('data-validate-realtime', 'true');
        }

        this.logger.debug(`Applied validation attributes to ${fieldConfig.name}:`, {
            required: fieldConfig.required,
            minLength: fieldConfig.minLength,
            maxLength: fieldConfig.maxLength,
            min: fieldConfig.min,
            max: fieldConfig.max,
            pattern: fieldConfig.pattern
        });
    }

    /**
     * Create form select - SCHEMA-DRIVEN
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Select element
     */
    createFormSelect(config) {
        const select = document.createElement('select');

        // Add empty option if not required
        if (!config.required) {
            const emptyOption = document.createElement('option');
            emptyOption.value = '';
            emptyOption.textContent = `Select ${config.label}...`;
            select.appendChild(emptyOption);
        }

        config.options?.forEach(option => {
            const optionElement = document.createElement('option');
            optionElement.value = option.value || option;
            optionElement.textContent = option.text || option;
            if ((option.value || option) === config.value) {
                optionElement.selected = true;
            }
            select.appendChild(optionElement);
        });
        return select;
    }

    /**
     * Create form input with datalist (HTML5 searchable select)
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Input element with datalist
     */
    createFormInputWithDatalist(config) {
        // Create text input (for display only, no name attribute)
        const input = document.createElement('input');
        input.type = 'text';
        input.id = `${this.entityName}-edit-${config.name}-display`;
        input.className = 'form-input';
        input.placeholder = config.placeholder || `Search or select ${config.label}...`;
        input.setAttribute('list', `${this.entityName}-${config.name}-datalist`);

        // Set initial display value if provided
        if (config.value) {
            if (config.searchable_data) {
                // Try to find matching option in dropdown
                const selectedOption = config.searchable_data.find(item => {
                    const itemValue = typeof item === 'string' ? item : (item.value || item.id);
                    return itemValue === config.value;
                });
                if (selectedOption) {
                    // Found in dropdown - use display text
                    input.value = typeof selectedOption === 'string' ? selectedOption :
                        (selectedOption.text || selectedOption.label || selectedOption.name || selectedOption.value);
                } else if (config.allowCustom) {
                    // Not found in dropdown but custom values allowed - use value directly (custom text)
                    input.value = config.value;
                }
            } else {
                // No searchable_data provided - use value directly
                input.value = config.value;
            }
        }

        // Create datalist
        const datalist = document.createElement('datalist');
        datalist.id = `${this.entityName}-${config.name}-datalist`;

        // Populate datalist with options
        const dataArray = config.searchable_data || [];
        dataArray.forEach(item => {
            // Skip empty values
            const itemValue = typeof item === 'string' ? item : (item.value || item.name || item.id);
            if (itemValue === '' || itemValue === null || itemValue === undefined) {
                return;
            }

            const option = document.createElement('option');
            option.value = typeof item === 'string' ? item :
                (item.text || item.label || item.name || item.value || item.id);

            // Store the actual ID in data-value attribute if it's an object
            if (typeof item === 'object' && item.value) {
                option.setAttribute('data-value', item.value);
            }

            datalist.appendChild(option);
        });

        // Create a hidden input to store the actual value (ID) for form submission
        // This is the actual form field that will be submitted
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.id = `${this.entityName}-edit-${config.name}`;
        hiddenInput.name = config.name;
        hiddenInput.value = config.value || '';

        // Check if custom entries are allowed (default: false = strict validation)
        const allowCustom = config.allowCustom || false;

        // Update hidden input when text input changes
        input.addEventListener('change', () => {
            // Find the matching option in datalist
            const selectedOption = Array.from(datalist.options).find(
                opt => opt.value === input.value
            );

            if (selectedOption && selectedOption.getAttribute('data-value')) {
                // Use the stored ID
                hiddenInput.value = selectedOption.getAttribute('data-value');
                input.setCustomValidity(''); // Clear any validation error
            } else if (selectedOption) {
                // Use the display text directly (for string arrays)
                hiddenInput.value = input.value;
                input.setCustomValidity(''); // Clear any validation error
            } else {
                // Not in datalist
                if (allowCustom) {
                    // Custom entries allowed - accept the value
                    hiddenInput.value = input.value;
                    input.setCustomValidity(''); // Clear any validation error
                } else {
                    // Custom entries NOT allowed - reject the value
                    hiddenInput.value = '';
                    if (input.value) {
                        input.setCustomValidity('Please select a valid option from the list');
                    } else {
                        input.setCustomValidity(''); // Empty is OK if not required
                    }
                }
            }
        });

        // Validate on blur as well (only if custom entries not allowed)
        if (!allowCustom) {
            input.addEventListener('blur', () => {
                const selectedOption = Array.from(datalist.options).find(
                    opt => opt.value === input.value
                );

                if (!selectedOption && input.value) {
                    input.setCustomValidity('Please select a valid option from the list');
                    input.reportValidity();
                }
            });
        }

        // Create container for input + hidden input + datalist
        const container = document.createElement('div');
        container.className = 'datalist-container';
        container.appendChild(input);
        container.appendChild(hiddenInput);
        container.appendChild(datalist);

        // Apply validation attributes to the visible input
        this.applyValidationAttributes(input, config);

        this.logger.debug(`Created datalist input for ${config.name} with ${dataArray.length} options`);

        return container;
    }

    /**
     * Create form textarea
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Textarea element
     */
    createFormTextarea(config) {
        const textarea = document.createElement('textarea');
        textarea.value = config.value || '';
        textarea.rows = config.rows || 4;
        return textarea;
    }

    /**
     * Create form checkbox
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Checkbox element
     */
    createFormCheckbox(config) {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = this._castToBoolean(config.value);
        return input;
    }

    _castToBoolean(value) {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const lowercased = value.toLowerCase().trim();
            return lowercased === 'true' || lowercased === '1' || lowercased === 'yes';
        }
        if (typeof value === 'number') {
            return value === 1;
        }
        return false;
    }

    /**
     * Create form number input - SCHEMA-DRIVEN
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Number input element
     */
    createFormNumber(config) {
        const input = document.createElement('input');
        input.type = config.type === 'integer' ? 'number' : 'number';
        input.value = config.value || '';
        
        // Integer step
        if (config.type === 'integer') {
            input.step = 1;
        }
        
        return input;
    }

    /**
     * Create form date input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Date input element
     */
    createFormDate(config) {
        const input = document.createElement('input');
        
        // Handle different date input types
        if (config.type === 'datetime-local') {
            input.type = 'datetime-local';
        } else {
            input.type = 'date';
        }
        
        input.value = config.value || '';
        return input;
    }

    /**
     * Create form email input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Email input element
     */
    createFormEmail(config) {
        const input = document.createElement('input');
        input.type = 'email';
        input.value = config.value || '';
        return input;
    }

    /**
     * Create form URL input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} URL input element
     */
    createFormUrl(config) {
        const input = document.createElement('input');
        input.type = 'url';
        input.value = config.value || '';
        return input;
    }

    /**
     * Create basic form input
     * @param {Object} config - Field configuration
     * @returns {HTMLElement} Input element
     */
    createFormInputBasic(config) {
        const input = document.createElement('input');
        input.type = config.type || 'text';
        input.value = config.value || '';
        return input;
    }

    /**
     * Create form buttons
     * @param {Array} buttons - Button configurations
     * @returns {HTMLElement} Button container
     */
    createFormButtons(buttons) {
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'button-container form-buttons';

        buttons.forEach(buttonConfig => {
            const button = document.createElement('button');
            button.textContent = buttonConfig.text;
            button.className = buttonConfig.className;
            button.id = buttonConfig.id;
            buttonContainer.appendChild(button);
        });

        return buttonContainer;
    }

    /**
     * Create action button
     * @param {Object} action - Action configuration
     * @param {boolean} includeText - Whether to include text
     * @returns {HTMLElement} Button element
     */
    createActionButton(action, includeText = true) {
        const button = document.createElement('button');

        // Use cssClass from action definition for consistent styling
        // Add icon-only class when text is not included (table buttons)
        const iconOnlyClass = includeText ? '' : 'icon-only';
        button.className = `action-button ${action.cssClass || action.name} ${iconOnlyClass}`.trim();
        button.setAttribute('data-action', action.name);
        button.setAttribute(`data-${this.entityName}-id`, action.entityId);
        button.setAttribute('title', action.title || action.name);

        const icon = document.createElement('span');
        icon.className = 'material-symbols-outlined';
        icon.textContent = action.icon || 'circle';
        button.appendChild(icon);

        // ALWAYS create button text for hover expansion
        if(includeText){
            const text = document.createElement('span');
            text.className = 'button-text';
            text.textContent = action.title;
            button.appendChild(text);
        }

        return button;
    }

    /**
     * Format field name for display
     * @param {string} fieldName - Field name
     * @returns {string} Formatted name
     */
    formatFieldName(fieldName) {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    /**
     * Format entity name for display
     * @returns {string} Formatted entity name
     */
    formatEntityName() {
        return this.entityName.charAt(0).toUpperCase() + this.entityName.slice(1);
    }

    /**
     * Create entity table row
     * @param {Object} rowData - Row data
     * @param {Object} tableConfig - Table configuration
     * @returns {HTMLElement} Table row
     */
    createEntityTableRow(rowData, tableConfig) {
        const tr = document.createElement('tr');
        tr.className = `${this.entityName}-table-row entity-table-row`;
        tr.dataset.entityId = rowData.id;
        
        // Add checkbox cell if selectable
        if (tableConfig.config && tableConfig.config.selectable) {
            const td = document.createElement('td');
            td.className = 'checkbox-column';
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.className = 'row-checkbox';
            checkbox.value = rowData.id;
            checkbox.addEventListener('change', (e) => this.handleRowSelection(e, tr));
            td.appendChild(checkbox);
            tr.appendChild(td);
        }
        
        // Add data cells
        tableConfig.columns.forEach(col => {
            const td = document.createElement('td');
            td.className = `column-${col.name}`;
            td.dataset.column = col.name;
            
            const value = rowData.data[col.name];
            
            // Apply type-specific formatting
            if (col.type === 'number' && value !== null && value !== undefined && value !== '-') {
                td.classList.add('numeric');
            } else if (col.type === 'date' && value) {
                td.classList.add('date');
            } else if (col.type === 'boolean') {
                td.classList.add('boolean');
                td.innerHTML = value ? 
                    '<span class="material-symbols-outlined text-success">check_circle</span>' : 
                    '<span class="material-symbols-outlined text-muted">cancel</span>';
                return;
            }
            
            // Set content
            if (col.html) {
                td.innerHTML = value || '-';
            } else {
                td.textContent = value || '-';
            }
            
            tr.appendChild(td);
        });
        
        // Add actions cell if needed
        if (tableConfig.config && tableConfig.config.showActions) {
            const td = document.createElement('td');
            td.className = 'actions-column';
            
            const actions = this.createTableRowActions(rowData);
            td.appendChild(actions);
            
            tr.appendChild(td);
        }
        
        // Add click handler if clickable
        if (tableConfig.config && tableConfig.config.clickable !== false) {
            tr.style.cursor = 'pointer';
            tr.addEventListener('click', (e) => {
                if (!e.target.closest('.action-button') && 
                    !e.target.closest('.row-checkbox') &&
                    !e.target.closest('.actions-column')) {
                    this.handleTableRowClick(e, rowData);
                }
            });
        }
        
        return tr;
    }



    /**
     * Create table row actions - FIXED: Now aligned with createActionButton
     * @param {Object} rowData - Row data
     * @returns {HTMLElement} Actions container
     */
    createTableRowActions(rowData) {
        const actions = document.createElement('div');
        actions.className = 'table-row-actions';
        
        // REAL FIX: Use the existing getCardActions method from BaseManager
        // This uses your cardActionVisibility configuration automatically!
        let tableActions = this.manager.getCardActions(rowData);
        
        // Create buttons using the standard createActionButton method
        tableActions.forEach(actionConfig => {
            const button = this.createActionButton({
                name: actionConfig.name,
                icon: actionConfig.icon,
                title: actionConfig.title,
                cssClass: actionConfig.cssClass,
                entityId: actionConfig.entityId
            }, false); // false = no text for table buttons
            
            actions.appendChild(button);
        });
        
        return actions;
    }


    /**
     * Handle table sorting
     * @param {Event} event - Click event
     * @param {HTMLElement} th - Table header element
     * @param {HTMLElement} table - Table element
     */
    handleTableSort(event, th, table) {
        const column = th.dataset.column;
        const currentDirection = th.dataset.sortDirection || 'none';
        
        // Determine new direction
        let newDirection = 'asc';
        if (currentDirection === 'asc') newDirection = 'desc';
        else if (currentDirection === 'desc') newDirection = 'none';
        
        // Reset all other columns
        const allHeaders = table.querySelectorAll('th.sortable');
        allHeaders.forEach(header => {
            if (header !== th) {
                header.dataset.sortDirection = 'none';
                header.querySelector('.sort-icon').textContent = 'unfold_more';
            }
        });
        
        // Update this column
        th.dataset.sortDirection = newDirection;
        const icon = th.querySelector('.sort-icon');
        if (newDirection === 'asc') {
            icon.textContent = 'arrow_upward';
        } else if (newDirection === 'desc') {
            icon.textContent = 'arrow_downward';
        } else {
            icon.textContent = 'unfold_more';
        }
        
        // Sort the table
        this.sortTable(table, column, newDirection);
    }

    /**
     * Sort table rows
     * @param {HTMLElement} table - Table element
     * @param {string} column - Column name
     * @param {string} direction - Sort direction
     */
    sortTable(table, column, direction) {
        const tbody = table.querySelector('tbody');
        const rows = Array.from(tbody.querySelectorAll('tr:not(.empty-state)'));
        
        if (direction === 'none') {
            // Restore original order (by data-entity-id)
            rows.sort((a, b) => {
                const aId = a.dataset.entityId;
                const bId = b.dataset.entityId;
                return aId.localeCompare(bId);
            });
        } else {
            rows.sort((a, b) => {
                const aCell = a.querySelector(`td[data-column="${column}"]`);
                const bCell = b.querySelector(`td[data-column="${column}"]`);
                
                if (!aCell || !bCell) return 0;
                
                let aValue = aCell.textContent.trim();
                let bValue = bCell.textContent.trim();
                
                // Handle numeric values
                if (aCell.classList.contains('numeric')) {
                    aValue = parseFloat(aValue.replace(/[^0-9.-]/g, '')) || 0;
                    bValue = parseFloat(bValue.replace(/[^0-9.-]/g, '')) || 0;
                }
                // Handle dates
                else if (aCell.classList.contains('date')) {
                    aValue = new Date(aValue).getTime() || 0;
                    bValue = new Date(bValue).getTime() || 0;
                }
                
                if (aValue < bValue) return direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        // Re-append rows in new order
        rows.forEach(row => tbody.appendChild(row));
    }

    /**
     * Handle select all checkbox
     * @param {Event} event - Change event
     * @param {HTMLElement} table - Table element
     */
    handleSelectAll(event, table) {
        const isChecked = event.target.checked;
        const checkboxes = table.querySelectorAll('tbody .row-checkbox');
        
        checkboxes.forEach(checkbox => {
            checkbox.checked = isChecked;
            const row = checkbox.closest('tr');
            if (isChecked) {
                row.classList.add('selected');
            } else {
                row.classList.remove('selected');
            }
        });
        
        // Emit selection change event
        const selectedIds = isChecked ? 
            Array.from(checkboxes).map(cb => cb.value) : [];
        
        const selectionEvent = new CustomEvent('tableSelectionChange', {
            detail: { selectedIds }
        });
        table.dispatchEvent(selectionEvent);
    }

    /**
     * Handle row selection
     * @param {Event} event - Change event
     * @param {HTMLElement} row - Table row
     */
    handleRowSelection(event, row) {
        if (event.target.checked) {
            row.classList.add('selected');
        } else {
            row.classList.remove('selected');
        }
        
        // Update select all checkbox
        const table = row.closest('table');
        const allCheckboxes = table.querySelectorAll('tbody .row-checkbox');
        const checkedBoxes = table.querySelectorAll('tbody .row-checkbox:checked');
        const selectAllCheckbox = table.querySelector('.select-all-checkbox');
        
        if (selectAllCheckbox) {
            selectAllCheckbox.checked = allCheckboxes.length === checkedBoxes.length;
            selectAllCheckbox.indeterminate = checkedBoxes.length > 0 && 
                                            checkedBoxes.length < allCheckboxes.length;
        }
        
        // Emit selection change event
        const selectedIds = Array.from(checkedBoxes).map(cb => cb.value);
        const selectionEvent = new CustomEvent('tableSelectionChange', {
            detail: { selectedIds }
        });
        table.dispatchEvent(selectionEvent);
    }

    /**
     * Handle table row click
     * @param {Event} event - Click event
     * @param {Object} rowData - Row data
     */
    handleTableRowClick(event, rowData) {
        const clickEvent = new CustomEvent('tableRowClick', {
            detail: { 
                entityId: rowData.id,
                rowData: rowData
            }
        });
        event.currentTarget.closest('table').dispatchEvent(clickEvent);
    }

    /**
     * Attach pagination event listeners
     * @param {HTMLElement} pagination - Pagination element
     * @param {Object} tableData - Table data
     */
    attachPaginationListeners(pagination, tableData) {
        const container = pagination.closest('.table-container-with-pagination');
        const table = container.querySelector('table');
        const pageSize = parseInt(container.dataset.pageSize);
        const totalRows = parseInt(container.dataset.totalRows);
        const totalPages = Math.ceil(totalRows / pageSize);
        
        // Previous button
        const prevBtn = pagination.querySelector('.prev-page');
        prevBtn.addEventListener('click', () => {
            const currentPage = parseInt(container.dataset.currentPage);
            if (currentPage > 1) {
                this.goToPage(container, currentPage - 1);
            }
        });
        
        // Next button
        const nextBtn = pagination.querySelector('.next-page');
        nextBtn.addEventListener('click', () => {
            const currentPage = parseInt(container.dataset.currentPage);
            if (currentPage < totalPages) {
                this.goToPage(container, currentPage + 1);
            }
        });
        
        // Page number buttons
        const pageButtons = pagination.querySelectorAll('.page-number');
        pageButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const page = parseInt(btn.dataset.page);
                this.goToPage(container, page);
            });
        });
    }


    /**
     * Create entity table
     * @param {Object} tableData - Table data with columns and rows
     * @returns {HTMLElement} Table element OR container with pagination
     */
    createEntityTable(tableData) {
        const table = document.createElement('table');
        table.className = `${this.entityName}-table entity-table`;
        
        // Create header
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        
        // Add checkbox column if selectable
        if (tableData.config && tableData.config.selectable) {
            const th = document.createElement('th');
            th.className = 'checkbox-column';
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.className = 'select-all-checkbox';
            checkbox.addEventListener('change', (e) => this.handleSelectAll(e, table));
            th.appendChild(checkbox);
            headerRow.appendChild(th);
        }
        
        // Add data columns
        tableData.columns.forEach(col => {
            const th = document.createElement('th');
            th.className = `column-${col.name}`;
            th.dataset.column = col.name;
            
            if (col.sortable !== false && tableData.config && tableData.config.sortable !== false) {
                th.classList.add('sortable');
                th.innerHTML = `
                    <span class="column-label">${col.label}</span>
                    <span class="sort-icon material-symbols-outlined">unfold_more</span>
                `;
                th.dataset.sortDirection = 'none';
                th.addEventListener('click', (e) => this.handleTableSort(e, th, table));
            } else {
                th.textContent = col.label;
            }
            
            if (col.width && col.width !== 'auto') {
                th.style.width = col.width;
            }
            
            headerRow.appendChild(th);
        });
        
        // Add actions column if needed
        if (tableData.config && tableData.config.showActions) {
            const th = document.createElement('th');
            th.className = 'actions-column';
            th.textContent = 'Actions';
            headerRow.appendChild(th);
        }
        
        thead.appendChild(headerRow);
        table.appendChild(thead);
        
        // Create body
        const tbody = document.createElement('tbody');
        tbody.className = 'entity-table-body';
        
        if (tableData.rows && tableData.rows.length > 0) {
            tableData.rows.forEach(row => {
                const tr = this.createEntityTableRow(row, tableData);
                tbody.appendChild(tr);
            });
        } else {
            // Empty state
            const tr = document.createElement('tr');
            const td = document.createElement('td');
            td.className = 'empty-state';
            td.colSpan = tableData.columns.length + 
                        (tableData.config?.selectable ? 1 : 0) + 
                        (tableData.config?.showActions ? 1 : 0);
            td.textContent = 'No data available';
            tr.appendChild(td);
            tbody.appendChild(tr);
        }
        
        table.appendChild(tbody);
        
        // Check if pagination is needed and valid
        if (tableData.config && tableData.config.pageSize && tableData.config.pageSize > 0) {
            return this.wrapTableWithPagination(table, tableData);
        }
        
        // No pagination - wrap in scrollable container
        const container = document.createElement('div');
        container.className = 'table-container';
        container.appendChild(table);
        
        return container;
    }

    /**
     * Wrap table with pagination controls
     * @param {HTMLElement} table - Table element
     * @param {Object} tableData - Table data
     * @returns {HTMLElement} Container with table and pagination
     */
    wrapTableWithPagination(table, tableData) {
        const container = document.createElement('div');
        container.className = 'table-container-with-pagination';
        
        // Table wrapper
        const tableWrapper = document.createElement('div');
        tableWrapper.className = 'table-container';
        tableWrapper.appendChild(table);
        container.appendChild(tableWrapper);
        
        // Only add pagination if there are rows
        if (tableData.rows && tableData.rows.length > 0) {
            // Store pagination state
            container.dataset.currentPage = '1';
            container.dataset.pageSize = String(tableData.config.pageSize || 25);
            container.dataset.totalRows = String(tableData.rows.length);
            
            // Create and add pagination controls
            const pagination = this.createPaginationControls(tableData, container);
            if (pagination) {
                container.appendChild(pagination);
                
                // Apply initial pagination
                this.applyPagination(table, 1, tableData.config.pageSize);
            }
        }
        
        return container;
    }

    /**
     * Create pagination controls
     * @param {Object} tableData - Table data
     * @param {HTMLElement} container - Container element for context
     * @returns {HTMLElement} Pagination controls
     */
    createPaginationControls(tableData, container) {
        const totalRows = tableData.rows ? tableData.rows.length : 0;
        const pageSize = tableData.config.pageSize || 25;
        const totalPages = Math.ceil(totalRows / pageSize);
        
        // Don't create pagination for single page or no data
        if (totalPages <= 1) {
            return null;
        }
        
        const pagination = document.createElement('div');
        pagination.className = 'table-pagination';
        
        // Page info
        const pageInfo = document.createElement('span');
        pageInfo.className = 'page-info';
        pageInfo.textContent = `Page 1 of ${totalPages} (${totalRows} items)`;
        pagination.appendChild(pageInfo);
        
        // Page controls
        const controls = document.createElement('div');
        controls.className = 'page-controls';
        
        // Previous button
        const prevBtn = document.createElement('button');
        prevBtn.className = 'page-button prev-page';
        prevBtn.innerHTML = '<span class="material-symbols-outlined">chevron_left</span>';
        prevBtn.disabled = true;
        controls.appendChild(prevBtn);
        
        // Page numbers
        const pageNumbers = document.createElement('div');
        pageNumbers.className = 'page-numbers';
        
        for (let i = 1; i <= Math.min(totalPages, 5); i++) {
            const pageBtn = document.createElement('button');
            pageBtn.className = 'page-button page-number';
            pageBtn.textContent = String(i);
            pageBtn.dataset.page = String(i);
            if (i === 1) pageBtn.classList.add('active');
            pageNumbers.appendChild(pageBtn);
        }
        
        controls.appendChild(pageNumbers);
        
        // Next button
        const nextBtn = document.createElement('button');
        nextBtn.className = 'page-button next-page';
        nextBtn.innerHTML = '<span class="material-symbols-outlined">chevron_right</span>';
        nextBtn.disabled = totalPages <= 1;
        controls.appendChild(nextBtn);
        
        pagination.appendChild(controls);
        
        // Add event listeners inline to avoid null reference issues
        this.attachPaginationListeners(pagination, tableData, container);
        
        return pagination;
    }

    /**
     * Attach pagination event listeners
     * @param {HTMLElement} pagination - Pagination element
     * @param {Object} tableData - Table data
     * @param {HTMLElement} container - Container element
     */
    attachPaginationListeners(pagination, tableData, container) {
        // Safety check
        if (!pagination || !container) {
            this.logger.warn('Cannot attach pagination listeners: missing pagination or container');
            return;
        }
        
        const table = container.querySelector('table');
        if (!table) {
            this.logger.warn('Cannot attach pagination listeners: table not found');
            return;
        }
        
        const pageSize = parseInt(container.dataset.pageSize || '25');
        const totalRows = parseInt(container.dataset.totalRows || '0');
        const totalPages = Math.ceil(totalRows / pageSize);
        
        // Previous button
        const prevBtn = pagination.querySelector('.prev-page');
        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                const currentPage = parseInt(container.dataset.currentPage || '1');
                if (currentPage > 1) {
                    this.goToPage(container, currentPage - 1);
                }
            });
        }
        
        // Next button
        const nextBtn = pagination.querySelector('.next-page');
        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                const currentPage = parseInt(container.dataset.currentPage || '1');
                if (currentPage < totalPages) {
                    this.goToPage(container, currentPage + 1);
                }
            });
        }
        
        // Page number buttons
        const pageButtons = pagination.querySelectorAll('.page-number');
        pageButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const page = parseInt(btn.dataset.page || '1');
                this.goToPage(container, page);
            });
        });
    }

    /**
     * Go to specific page
     * @param {HTMLElement} container - Container element
     * @param {number} page - Page number
     */
    goToPage(container, page) {
        if (!container) {
            this.logger.warn('Cannot go to page: container is null');
            return;
        }
        
        const table = container.querySelector('table');
        const pagination = container.querySelector('.table-pagination');
        
        if (!table) {
            this.logger.warn('Cannot go to page: table not found');
            return;
        }
        
        const pageSize = parseInt(container.dataset.pageSize || '25');
        const totalRows = parseInt(container.dataset.totalRows || '0');
        const totalPages = Math.ceil(totalRows / pageSize);
        
        // Validate page number
        if (page < 1 || page > totalPages) {
            this.logger.warn(`Invalid page number: ${page}`);
            return;
        }
        
        // Apply pagination
        this.applyPagination(table, page, pageSize);
        
        // Update state
        container.dataset.currentPage = String(page);
        
        // Update controls if pagination exists
        if (pagination) {
            const prevBtn = pagination.querySelector('.prev-page');
            const nextBtn = pagination.querySelector('.next-page');
            
            if (prevBtn) prevBtn.disabled = page === 1;
            if (nextBtn) nextBtn.disabled = page === totalPages;
            
            // Update page info
            const pageInfo = pagination.querySelector('.page-info');
            if (pageInfo) {
                const start = (page - 1) * pageSize + 1;
                const end = Math.min(page * pageSize, totalRows);
                pageInfo.textContent = `Page ${page} of ${totalPages} (${start}-${end} of ${totalRows})`;
            }
            
            // Update page buttons
            const pageButtons = pagination.querySelectorAll('.page-number');
            pageButtons.forEach(btn => {
                btn.classList.toggle('active', parseInt(btn.dataset.page || '0') === page);
            });
        }
    }

    /**
     * Apply pagination to table
     * @param {HTMLElement} table - Table element
     * @param {number} page - Page number
     * @param {number} pageSize - Page size
     */
    applyPagination(table, page, pageSize) {
        if (!table) {
            this.logger.warn('Cannot apply pagination: table is null');
            return;
        }
        
        const tbody = table.querySelector('tbody');
        if (!tbody) {
            this.logger.warn('Cannot apply pagination: tbody not found');
            return;
        }
        
        const rows = Array.from(tbody.querySelectorAll('tr:not(.empty-state)'));
        
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        
        rows.forEach((row, index) => {
            if (index >= start && index < end) {
                row.style.display = '';
            } else {
                row.style.display = 'none';
            }
        });
    }

    /**
     * Create confirmation dialog
     * @param {Object} options - Dialog options
     * @param {string} options.title - Dialog title
     * @param {string} options.message - Confirmation message
     * @param {string} options.confirmText - Confirm button text (default: "Delete")
     * @param {string} options.cancelText - Cancel button text (default: "Cancel")
     * @param {string} options.type - Dialog type: 'danger', 'warning', 'info' (default: 'danger')
     * @returns {HTMLElement} Confirmation dialog element
     */
    createConfirmationDialog(options = {}) {
        const {
            title = 'Confirm Action',
            message = 'Are you sure you want to proceed?',
            confirmText = 'Delete',
            cancelText = 'Cancel',
            type = 'danger'
        } = options;

        const dialog = document.createElement('div');
        dialog.className = 'confirmation-dialog-wrapper';
        dialog.innerHTML = `
            <div class="confirmation-dialog ${type}">
                <div class="confirmation-dialog-header">
                    <h3 class="confirmation-dialog-title">${title}</h3>
                </div>
                <div class="confirmation-dialog-body">
                    <p class="confirmation-dialog-message">${message}</p>
                </div>
                <div class="confirmation-dialog-footer">
                    <button class="btn btn-secondary confirmation-cancel" data-action="cancel">
                        <span class="material-symbols-outlined">close</span>
                        ${cancelText}
                    </button>
                    <button class="btn btn-secondary btn-${type} confirmation-confirm" data-action="confirm">
                        <span class="material-symbols-outlined">${type === 'danger' ? 'delete' : 'check'}</span>
                        ${confirmText}
                    </button>
                </div>
            </div>
        `;

        this.logger.ui(`Created confirmation dialog: ${title}`);
        return dialog;
    }


}

export default BaseViews;